import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcInventoryV1BooksOrderQueryServiceBooksOrderGet } from '@interxfi/api-client/generated/hooks/ixc-inventory-v1-books-order-query-service-books-order-get';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcInventoryV1BooksOrderState } from '@interxfi/types/generated/api';
import {
  BooksOrderEventDrawer,
  BooksOrderEventSummary,
} from '../../../../../../../../../../features/inventory/books-order/inventory-books-order-event';
import {
  getInventoryBooksOrderAmountValueListItem,
  getInventoryBooksOrderStateListItems,
} from '../../../../../../../../../../features/inventory/books-order/inventory-books-order-list-items';
import { getInventoryBooksOrderDetailPath } from '../../../../../../../../../../features/inventory/books-order/inventory-books-order-routing';
import { InventoryBooksOrderStatusPill } from '../../../../../../../../../../features/inventory/books-order/inventory-books-order-status-pill';
import { InventoryBooksOrderSummaryBar } from '../../../../../../../../../../features/inventory/books-order/inventory-books-order-summary-bar';
import { getInventoryExecutionEventListItems } from '../../../../../../../../../../features/inventory/shared-inventory-list-items';
import { OrderSidePill } from '../../../../../../../../../../features/order/order-side-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute(
  '/_app/_clearing-core/_clearing-required/clearing/operations/_operations/inventory/books-order/_books-order/$booksOrderId',
)({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `books-order-${params.booksOrderId}`,
        label: 'Books Order',
        to: getInventoryBooksOrderDetailPath(params.booksOrderId),
        suffix: (
          <CopyButton
            content={params.booksOrderId}
            label="Copy Books Order ID"
            tooltip={{ copy: params.booksOrderId, copied: 'Copied Books Order ID' }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Books Order'),
  component: InventoryBooksOrderDetail,
});

function getListItems(data: IxcInventoryV1BooksOrderState | undefined) {
  const listItems = getInventoryBooksOrderStateListItems(data);

  return {
    general: {
      left: [
        ...(listItems.data.asset.summary ? [listItems.data.asset.summary] : []),
        listItems.data.spec.sidePill,
        getInventoryBooksOrderAmountValueListItem(listItems.data.spec.amount),
        listItems.data.spec.price,
        listItems.data.spec.priceSource,
        listItems.data.grossAmount,
        ...(listItems.data.principalAmount ? [{ ...listItems.data.principalAmount.value.value, title: 'Principal Amount' }] : []),
        listItems.data.kind,
        listItems.data.spec.accountId,
      ] satisfies ReadonlyListItem[],
      right: [
        listItems.metadata.createdAt,
        listItems.metadata.updatedAt,
        listItems.booksOrderId,
        listItems.orderId,
        listItems.data.firmOrderId,
        listItems.assetId,
        listItems.data.queuedAt,
      ] satisfies ReadonlyListItem[],
    },
    principalExecution: getInventoryExecutionEventListItems(listItems.data.principalExecution),
    risklessPrincipalExecution: getInventoryExecutionEventListItems(listItems.data.risklessPrincipalExecution),
  };
}

function getMenuItems(booksOrderId: string): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: { Component: BooksOrderEventDrawer, props: { request: { booksOrderId } } },
    },
    actions: undefined,
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load books order', 'view this books order');

function InventoryBooksOrderDetail() {
  const { booksOrderId } = Route.useParams();
  const request = { booksOrderId } as const;
  const { data, error, isPending } = useIxcInventoryV1BooksOrderQueryServiceBooksOrderGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.booksOrder), [data?.booksOrder]);
  const menuItems = useMemo(() => getMenuItems(booksOrderId), [booksOrderId]);

  return (
    <PageLayout gap={7} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <InventoryBooksOrderStatusPill isPending={showPending} size="large" status={data?.booksOrder?.status} />
            <OrderSidePill isPending={showPending} side={data?.booksOrder?.data?.spec?.side} size="large" />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} isPending={showPending} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <InventoryBooksOrderSummaryBar booksOrderId={booksOrderId} />
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <BooksOrderEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>General</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.general.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      {(showPending || Boolean(data?.booksOrder?.data?.principalExecution)) && (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Principal Execution</Typography.Level3>
            <AppReadonlyList isPending={showPending} items={listItems.principalExecution} />
          </VerticalStack>
        </PageLayout.Section>
      )}

      {(showPending || Boolean(data?.booksOrder?.data?.risklessPrincipalExecution)) && (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Riskless Principal Execution</Typography.Level3>
            <AppReadonlyList isPending={showPending} items={listItems.risklessPrincipalExecution} />
          </VerticalStack>
        </PageLayout.Section>
      )}
    </PageLayout>
  );
}
